import { useNavigate, useParams } from "react-router-dom";
import { BackButton } from "@/components/BackButton";
import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useLazyTableParams } from "@/lib/useLazyTableParams";
import { useListPageParam } from "@/lib/useListPageParam";
import { useListSearchParam } from "@/lib/useListSearchParam";
import { useListGroupParam } from "@/lib/useListGroupParam";
import {
  downloadEventParticipantsExport,
  eqFilter,
  isNullEqFilter,
  joinAnd,
  joinOr,
  likeFilter,
  NO_GROUP_FILTER_VALUE,
  useAdminEvent,
  useAdminEventBoats,
  useAdminEventParticipantGroups,
  useAdminEventParticipantStats,
  useAdminParticipantsList,
  type AdminBoat,
  type AdminBoatCrewMember,
  type AdminParticipantListItem,
} from "@/lib/admin-api";
import {
  ageInYearsAtEventStart,
  participantHasMedicalInformation,
} from "@/lib/utils";
import { Toast } from "primereact/toast";
import { useToast } from "@/lib/ui";

const EVENT_PARTICIPANTS_PAGE_SIZE = 10;

type ParticipantStatus = "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED";
type TagSeverity =
  | "success"
  | "info"
  | "warning"
  | "danger"
  | "secondary"
  | "contrast"
  | null
  | undefined;

type MedicalParticipant = AdminParticipantListItem | AdminBoatCrewMember;

function participantStatusSeverity(status: ParticipantStatus): TagSeverity {
  const severityByStatus: Record<ParticipantStatus, TagSeverity> = {
    PENDING: "warning",
    APPROVED: "success",
    REJECTED: "danger",
    CANCELLED: "secondary",
  };
  return severityByStatus[status] ?? "info";
}

function StatusBadge({ status }: { status: string | undefined }) {
  if (!status) return null;
  if (status === "MIXED") {
    return <Tag value={status} severity="warning" />;
  }
  return (
    <Tag
      value={status}
      severity={participantStatusSeverity(status as ParticipantStatus)}
    />
  );
}

function participantMatchesGroupFilter(
  participant: { group?: string | null },
  groupFilter: string,
): boolean {
  const normalizedGroupFilter = groupFilter.trim();
  if (!normalizedGroupFilter) return true;
  if (normalizedGroupFilter === NO_GROUP_FILTER_VALUE) {
    return !participant.group?.trim();
  }
  return (participant.group?.trim() ?? "") === normalizedGroupFilter;
}

function boatCountsTowardCapacity(status: AdminBoat["status"]): boolean {
  return status === "PENDING" || status === "APPROVED";
}

export function EventParticipants() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toastRef, showToast } = useToast();
  const [globalFilter, setGlobalFilter] = useListSearchParam("q");
  const [groupFilter, setGroupFilter] = useListGroupParam();
  const [medicalDialogParticipant, setMedicalDialogParticipant] =
    useState<MedicalParticipant | null>(null);
  const [pageFromUrl, setPageFromUrl] = useListPageParam();
  const [expandedBoatRows, setExpandedBoatRows] = useState<Record<string, boolean> | null>(
    null,
  );
  const [exporting, setExporting] = useState(false);

  const {
    lazyParams,
    setLazyParams,
    onPage: onPageBase,
    onSort,
    resetToFirstPage: resetToFirstPageBase,
  } = useLazyTableParams({
    initialRows: EVENT_PARTICIPANTS_PAGE_SIZE,
    initialSortField: "createdAt",
    initialSortOrder: -1,
    initialFirst: (Math.max(1, pageFromUrl) - 1) * EVENT_PARTICIPANTS_PAGE_SIZE,
  });

  const onPage = useCallback(
    (event: { first: number; rows: number; page?: number }) => {
      onPageBase(event);
      const newPage = (event.page ?? Math.floor(event.first / event.rows)) + 1;
      setPageFromUrl(newPage);
    },
    [onPageBase, setPageFromUrl],
  );

  const resetToFirstPage = useCallback(() => {
    resetToFirstPageBase();
    setPageFromUrl(1);
  }, [resetToFirstPageBase, setPageFromUrl]);

  useEffect(() => {
    setLazyParams((prev) => {
      const expectedFirst = (Math.max(1, pageFromUrl) - 1) * prev.rows;
      if (prev.first === expectedFirst) return prev;
      return {
        ...prev,
        first: expectedFirst,
        page: Math.max(0, pageFromUrl - 1),
      };
    });
  }, [pageFromUrl, setLazyParams]);

  useEffect(() => {
    if (!id) {
      void navigate("/events");
    }
  }, [id, navigate]);

  const eventQuery = useAdminEvent(id);
  const event = eventQuery.data?.event ?? null;
  const loading = eventQuery.isLoading;
  const groupsEnabled = event?.groupsEnabled ?? false;
  const isRaceEvent = (event as { eventKind?: string } | null)?.eventKind === "RACE";

  const participantStatsQuery = useAdminEventParticipantStats(id);
  const participantStats = participantStatsQuery.data;
  const participantGroupsQuery = useAdminEventParticipantGroups(
    groupsEnabled ? id : undefined,
  );
  const boatsQuery = useAdminEventBoats(isRaceEvent ? id : undefined);

  useEffect(() => {
    if (!id) return;
    if (eventQuery.isError) {
      void navigate("/events");
      return;
    }
    if (
      eventQuery.isSuccess &&
      !eventQuery.isFetching &&
      !eventQuery.data?.event
    ) {
      void navigate("/events");
    }
  }, [
    eventQuery.data,
    eventQuery.isError,
    eventQuery.isFetching,
    eventQuery.isSuccess,
    id,
    navigate,
  ]);

  const filter = useMemo(() => {
    if (!id || isRaceEvent) return undefined;
    const search = globalFilter.trim();
    const searchOr =
      search !== ""
        ? joinOr([
            likeFilter("firstName", search),
            likeFilter("lastName", search),
          ])
        : undefined;

    const groupEq = (() => {
      const normalized = groupFilter.trim();
      if (!normalized) return undefined;
      if (normalized === NO_GROUP_FILTER_VALUE) {
        return isNullEqFilter("group");
      }
      return eqFilter("group", normalized);
    })();

    return joinAnd([
      eqFilter("eventId", id),
      ...(searchOr ? [searchOr] : []),
      ...(groupEq ? [groupEq] : []),
    ]);
  }, [globalFilter, groupFilter, id, isRaceEvent]);

  const groupFilterOptions = useMemo(() => {
    const groups = participantGroupsQuery.data?.groups ?? [];
    return [
      { label: "All groups", value: "" },
      { label: "No group", value: NO_GROUP_FILTER_VALUE },
      ...groups.map((group) => ({ label: group, value: group })),
    ];
  }, [participantGroupsQuery.data?.groups]);

  const groupStats = groupsEnabled ? participantStats?.byGroup : undefined;

  const participantsQuery = useAdminParticipantsList(
    {
      page: lazyParams.page + 1,
      pageSize: lazyParams.rows,
      sortField: lazyParams.sortField,
      sortOrder: lazyParams.sortOrder,
      filter,
    },
    { enabled: !!event && !isRaceEvent },
  );
  const participants = participantsQuery.data?.data ?? [];
  const totalRecords = participantsQuery.data?.pagination.total ?? 0;
  const participantsLoading = participantsQuery.isFetching;

  const filteredBoats = useMemo(() => {
    const boats = boatsQuery.data?.boats ?? [];
    const search = globalFilter.trim().toLowerCase();
    const normalizedGroupFilter = groupFilter.trim();
    return boats.filter((boat) => {
      if (normalizedGroupFilter) {
        const hasMatchingCrew = boat.crew.some((member) =>
          participantMatchesGroupFilter(member, normalizedGroupFilter),
        );
        if (!hasMatchingCrew) return false;
      }
      if (!search) return true;
      const boatText = `${boat.class} ${boat.sailNumber}`.toLowerCase();
      if (boatText.includes(search)) return true;
      return boat.crew.some((member) =>
        `${member.firstName} ${member.lastName}`.toLowerCase().includes(search),
      );
    });
  }, [boatsQuery.data?.boats, globalFilter, groupFilter]);

  const displayStats = useMemo(() => {
    if (isRaceEvent) {
      if (!boatsQuery.isSuccess || !boatsQuery.data?.boats) return null;
      const boats = boatsQuery.data.boats;
      const countByPricePoint = new Map<string | null, number>();
      let total = 0;
      for (const boat of boats) {
        if (!boatCountsTowardCapacity(boat.status)) continue;

        total++;
        const ppId = boat.pricePoint?.id ?? "null";
        countByPricePoint.set(ppId, (countByPricePoint.get(ppId) ?? 0) + 1);
      }

      const byPricePoint: { eventPricePointId: string | null; label: string; count: number }[] =
        event?.pricePoints?.map((pp) => ({
          eventPricePointId: pp.id,
          label: pp.label,
          count: countByPricePoint.get(pp.id) ?? 0,
        })) ?? [];

      const nullCount = countByPricePoint.get("null") ?? 0;
      if (nullCount > 0) {
        byPricePoint.push({
          eventPricePointId: null,
          label: "No price point",
          count: nullCount,
        });
      }

      return { total, byPricePoint };
    }
    return participantStats;
  }, [
    isRaceEvent,
    boatsQuery.isSuccess,
    boatsQuery.data?.boats,
    participantStats,
    event?.pricePoints,
  ]);

  const onFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
    if (!isRaceEvent) {
      resetToFirstPage();
    }
  };

  const onGroupFilterChange = (value: string) => {
    setGroupFilter(value, { resetPage: !isRaceEvent });
    if (!isRaceEvent) {
      resetToFirstPageBase();
    }
  };

  const onDownloadExcel = async () => {
    if (!id || exporting) return;
    setExporting(true);
    try {
      await downloadEventParticipantsExport(id);
    } catch (error) {
      showToast({
        severity: "error",
        summary: "Export failed",
        detail:
          error instanceof Error ? error.message : "Could not download spreadsheet",
        life: 5000,
      });
    } finally {
      setExporting(false);
    }
  };

  const statusBodyTemplate = (rowData: AdminParticipantListItem) => {
    const status: ParticipantStatus = (rowData.status ??
      "PENDING") as ParticipantStatus;
    return <Tag value={status} severity={participantStatusSeverity(status)} />;
  };

  const nameBodyTemplate = (rowData: AdminParticipantListItem) => {
    return `${rowData.firstName} ${rowData.lastName}`;
  };

  const pricePointBodyTemplate = (rowData: AdminParticipantListItem) => {
    return rowData.pricePoint?.label ?? "—";
  };

  const groupBodyTemplate = (rowData: { group?: string | null }) => {
    return rowData.group?.trim() ? rowData.group : "—";
  };

  const ageBodyTemplate = (rowData: MedicalParticipant) => {
    return rowData.consentFormParticipantDateOfBirth && event?.startsAt && event?.timezone
      ? ageInYearsAtEventStart(rowData.consentFormParticipantDateOfBirth, event.startsAt, event.timezone) + " yoa"
      : "Adult";
  };

  const photoConsentBodyTemplate = (rowData: MedicalParticipant) => {
    return rowData.photoConsent ? (
      <Tag value="Yes" severity="success" />
    ) : (
      <Tag value="No" severity="danger" />
    );
  };

  const medicalInformationBodyTemplate = (rowData: MedicalParticipant) => {
    if (!participantHasMedicalInformation(rowData)) {
      return <span className="text-color-secondary">—</span>;
    }
    const name = `${rowData.firstName} ${rowData.lastName}`;
    return (
      <button
        type="button"
        className="border-none bg-transparent p-0 cursor-pointer"
        aria-label={`View medical information for ${name}`}
        onClick={() => setMedicalDialogParticipant(rowData)}
      >
        <Tag
          value="Medical"
          severity="warning"
          icon="pi pi-exclamation-triangle"
        />
      </button>
    );
  };

  const actionBodyTemplate = (rowData: AdminParticipantListItem) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          onClick={() => {
            void navigate(`/participants/${rowData.id}`);
          }}
          aria-label="View"
          title={`View ${rowData.firstName} ${rowData.lastName}`}
        />
      </div>
    );
  };

  const boatRowExpansionTemplate = (boat: AdminBoat) => {
    const normalizedGroupFilter = groupFilter.trim();
    const crew =
      normalizedGroupFilter !== ""
        ? boat.crew.filter((member) =>
            participantMatchesGroupFilter(member, normalizedGroupFilter),
          )
        : boat.crew;
    return (
      <CrewMembersTable
        crew={crew}
        groupsEnabled={groupsEnabled}
        onNavigate={navigate}
        ageBodyTemplate={ageBodyTemplate}
        photoConsentBodyTemplate={photoConsentBodyTemplate}
        medicalInformationBodyTemplate={medicalInformationBodyTemplate}
        groupBodyTemplate={groupBodyTemplate}
      />
    );
  };

  if (loading) {
    return (
      <div className="card">
        <div className="text-center p-4">Loading...</div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  return (
    <div className="card">
      <Toast ref={toastRef} />
      <div className="mb-4">
      <ParticipantsHeader
        eventTitle={event.title}
        exporting={exporting}
        onDownloadExcel={() => {
          void onDownloadExcel();
        }}
      />

        {(isRaceEvent ? boatsQuery.isFetching : participantStatsQuery.isLoading) && (
          <p className="text-color-secondary m-0 mb-3">
            Loading summary…
          </p>
        )}
        {(isRaceEvent ? boatsQuery.isError : participantStatsQuery.isError) && (
          <p className="text-color-secondary m-0 mb-3">
            Could not load summary.
          </p>
        )}
        {displayStats && (
          <div className="mb-3">
            <p className="m-0 mb-2">
              <span className="font-semibold">{displayStats.total}</span>{" "}
              {isRaceEvent
                ? displayStats.total === 1
                  ? "boat"
                  : "boats"
                : displayStats.total === 1
                  ? "participant"
                  : "participants"}
            </p>
            {(event.pricePoints?.length ?? 0) > 1 && (
              <DataTable
                value={displayStats.byPricePoint.filter(
                  (row) => row.count > 0,
                )}
                dataKey="eventPricePointId"
                size="small"
                showGridlines
                className="text-sm"
                style={{ maxWidth: "min(560px, 100%)" }}
                emptyMessage={`No ${isRaceEvent ? "boats" : "participants"} by price point`}
              >
                <Column field="label" header="Price point" />
                <Column
                  field="count"
                  header="Count"
                  body={(row: typeof displayStats.byPricePoint[number]) => row.count}
                  headerStyle={{ width: "6rem", textAlign: "right" }}
                  bodyStyle={{ textAlign: "right" }}
                />
              </DataTable>
            )}
            {groupStats && groupStats.length > 0 && (
              <DataTable
                value={groupStats}
                dataKey="label"
                size="small"
                showGridlines
                className="text-sm mt-2"
                style={{ maxWidth: "min(560px, 100%)" }}
                emptyMessage="No participants by group"
              >
                <Column field="label" header="Group" />
                <Column
                  field="count"
                  header="Participants"
                  body={(row: (typeof groupStats)[number]) => row.count}
                  headerStyle={{ width: "8rem", textAlign: "right" }}
                  bodyStyle={{ textAlign: "right" }}
                />
              </DataTable>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-4 gap-4">
        <span className="p-input-icon-left w-full md:w-auto">
          <i className="pi pi-search" />
          <InputText
            value={globalFilter}
            onChange={onFilter}
            placeholder={isRaceEvent ? "Search boats or crew..." : "Search participants..."}
            className="w-full"
          />
        </span>
        {groupsEnabled ? (
          <Dropdown
            value={groupFilter}
            options={groupFilterOptions}
            optionLabel="label"
            optionValue="value"
            onChange={(event) => {
              const value =
                typeof event.value === "string" ? event.value : "";
              onGroupFilterChange(value);
            }}
            placeholder="Filter by group"
            className="w-full md:w-14rem"
          />
        ) : null}
      </div>

      {isRaceEvent ? (
        <DataTable
          value={filteredBoats}
          dataKey="id"
          loading={boatsQuery.isFetching}
          responsiveLayout="scroll"
          emptyMessage="No boats found"
          className="text-sm"
          expandedRows={expandedBoatRows ?? {}}
          onRowToggle={(e) => setExpandedBoatRows(e.data as Record<string, boolean>)}
          rowExpansionTemplate={boatRowExpansionTemplate}
        >
          <Column expander style={{ width: "3rem" }} />
          <Column field="class" header="Class" style={{ minWidth: "160px" }} />
          <Column field="sailNumber" header="Sail number" style={{ minWidth: "140px" }} />
          <Column
            header="Price point"
            body={(boat: AdminBoat) => boat.pricePoint?.label ?? "—"}
            style={{ minWidth: "120px" }}
          />
          <Column
            header="Crew"
            body={(boat: AdminBoat) => boat.crew.length}
            style={{ minWidth: "80px" }}
          />
          <Column
            header="Status"
            body={(boat: AdminBoat) => <StatusBadge status={boat.status} />}
            style={{ minWidth: "120px" }}
          />
        </DataTable>
      ) : (
        <DataTable
          value={participants}
          lazy
          paginator
          rows={lazyParams.rows}
          first={lazyParams.first}
          totalRecords={totalRecords}
          onPage={onPage}
          onSort={onSort}
          sortField={lazyParams.sortField}
          sortOrder={lazyParams.sortOrder}
          loading={participantsLoading}
          responsiveLayout="scroll"
          emptyMessage="No participants found"
          className="text-sm"
        >
          <Column
            field="name"
            header="Name"
            body={nameBodyTemplate}
            sortable
            style={{ minWidth: "200px" }}
          />
          <Column
            field="status"
            header="Status"
            body={statusBodyTemplate}
            sortable
            style={{ minWidth: "120px" }}
          />
          <Column
            field="pricePoint.label"
            header="Price point"
            body={pricePointBodyTemplate}
            style={{ minWidth: "140px" }}
          />
          {groupsEnabled ? (
            <Column
              field="group"
              header="Group"
              body={groupBodyTemplate}
              style={{ minWidth: "140px" }}
            />
          ) : null}
          <Column
            field="age"
            header="Age"
            body={ageBodyTemplate}
            style={{ minWidth: "160px" }}
          />
          <Column
            field="photoConsent"
            header="Photo Consent"
            body={photoConsentBodyTemplate}
            style={{ minWidth: "160px" }}
          />
          <Column
            field="medicalInformation"
            header="Medical Information"
            body={medicalInformationBodyTemplate}
            style={{ minWidth: "160px" }}
          />
          <Column
            body={actionBodyTemplate}
            header="Actions"
            style={{ minWidth: "100px" }}
          />
        </DataTable>
      )}

      <Dialog
        header={
          medicalDialogParticipant
            ? `Medical information — ${medicalDialogParticipant.firstName} ${medicalDialogParticipant.lastName}`
            : "Medical information"
        }
        visible={medicalDialogParticipant !== null}
        onHide={() => setMedicalDialogParticipant(null)}
        style={{ width: "min(560px, 95vw)" }}
        dismissableMask
        footer={
          <Button
            type="button"
            label="Close"
            onClick={() => setMedicalDialogParticipant(null)}
          />
        }
      >
        {medicalDialogParticipant && (
          <div className="flex flex-column gap-3">
            {medicalDialogParticipant.consentFormImportantMedicalInformation
              ?.trim() && (
              <MedicalSection
                label="Important Medical Information:"
                value={medicalDialogParticipant.consentFormImportantMedicalInformation}
              />
            )}
            {medicalDialogParticipant.consentFormMedicationDetails?.trim() && (
              <MedicalSection
                label="Medication:"
                value={medicalDialogParticipant.consentFormMedicationDetails}
              />
            )}
            {medicalDialogParticipant.consentFormInjuriesDetails?.trim() && (
              <MedicalSection
                label="Injuries:"
                value={medicalDialogParticipant.consentFormInjuriesDetails}
              />
            )}
            {medicalDialogParticipant.consentFormDietaryRequirementsDetails?.trim() && (
              <MedicalSection
                label="Dietary Requirements:"
                value={medicalDialogParticipant.consentFormDietaryRequirementsDetails}
              />
            )}
            {medicalDialogParticipant.consentFormDisabilityDetails?.trim() && (
              <MedicalSection
                label="Disability:"
                value={medicalDialogParticipant.consentFormDisabilityDetails}
              />
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}

function MedicalSection(props: { label: string; value: string }) {
  return (
    <div>
      <span className="font-semibold">{props.label} </span>
      <pre className="text-sm mt-1 mb-0 whitespace-pre-wrap">{props.value}</pre>
    </div>
  );
}

function ParticipantsHeader(props: {
  eventTitle: string;
  exporting: boolean;
  onDownloadExcel: () => void;
}) {
  return (
    <div className="flex align-items-center gap-3 mb-4 flex-wrap">
      <BackButton />
      <h1 className="text-2xl font-bold m-0 min-w-0 flex-1">
        Participants - {props.eventTitle}
      </h1>
      <Button
        label="Download Excel"
        icon="pi pi-download"
        className="p-button-outlined"
        loading={props.exporting}
        disabled={props.exporting}
        onClick={props.onDownloadExcel}
      />
    </div>
  );
}

function CrewMembersTable(props: {
  crew: AdminBoatCrewMember[];
  groupsEnabled: boolean;
  onNavigate: ReturnType<typeof useNavigate>;
  ageBodyTemplate: (rowData: MedicalParticipant) => string;
  photoConsentBodyTemplate: (rowData: MedicalParticipant) => ReactElement;
  medicalInformationBodyTemplate: (rowData: MedicalParticipant) => ReactElement;
  groupBodyTemplate: (rowData: { group?: string | null }) => string;
}) {
  return (
    <div className="p-3 bg-gray-50 border-round border-1 border-300">
      <h4 className="mt-0 mb-3">Crew Members</h4>
      <DataTable value={props.crew} dataKey="id" className="text-sm">
        <Column
          field="name"
          header="Name"
          body={(member: AdminBoatCrewMember) => `${member.firstName} ${member.lastName}`}
        />
        <Column
          header="Status"
          body={(member: AdminBoatCrewMember) => (
            <StatusBadge status={member.status ?? undefined} />
          )}
        />
        {props.groupsEnabled ? (
          <Column
            header="Group"
            body={(member: AdminBoatCrewMember) => props.groupBodyTemplate(member)}
          />
        ) : null}
        <Column
          header="Age"
          body={(member: AdminBoatCrewMember) => props.ageBodyTemplate(member)}
        />
        <Column
          header="Photo Consent"
          body={(member: AdminBoatCrewMember) => props.photoConsentBodyTemplate(member)}
        />
        <Column
          header="Medical Information"
          body={(member: AdminBoatCrewMember) => props.medicalInformationBodyTemplate(member)}
        />
        <Column
          header="Actions"
          body={(member: AdminBoatCrewMember) => (
            <div className="flex gap-2">
              <Button
                icon="pi pi-eye"
                className="p-button-text p-button-sm"
                onClick={() => {
                  void props.onNavigate(`/participants/${member.id}`);
                }}
                aria-label="View"
                title={`View ${member.firstName} ${member.lastName}`}
              />
            </div>
          )}
        />
      </DataTable>
    </div>
  );
}
